import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useFeature } from '../../../hooks/useFeature';
import { useAdminPilotageData } from '../../../hooks/useAdminPilotageData';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import PeriodSelector, { PERIODS } from '../../../components/admin-v2/PeriodSelector';
import HeroIntegrated from '../../../components/admin-v2/HeroIntegrated';
import IndicatorsGrid from '../../../components/admin-v2/IndicatorsGrid';
import PilotageCreatorsTable from '../../../components/admin-v2/PilotageCreatorsTable';
import LiveStripStub from '../../../components/admin-v2/LiveStripStub';
import LiveNowCardsStub from '../../../components/admin-v2/LiveNowCardsStub';

/**
 * Admin V2 Pilotage — Page principale de monitoring multi-créateurs (Sprint 2).
 *
 * Modules :
 *  - HeroIntegrated         : KPIs principaux (créateurs actifs, sessions, hours, recrutes)
 *  - LiveStripStub          : feature-gated (`pages.manager.live`)
 *  - IndicatorsGrid         : KPIs avancés (followers, matches, go-live rate, avg session/heures)
 *  - LiveNowCardsStub       : feature-gated (`pages.manager.live`)
 *  - PilotageCreatorsTable  : liste paginée + sortable des créateurs sur la période
 *
 * Data source : `useAdminPilotageData` (Excel injecté — creators, creator_metrics_daily, creator_current_month).
 *
 * Route : `/admin/dashboard` (bascule depuis V1) ET `/admin/v2/pilotage` (route directe).
 * Protégée par `<FeatureGuard feature="pages.admin.pilotage">` côté Routes.jsx.
 *
 * TCN : ce composant n'est PAS chargé pour TCN (le routing pointe vers la
 * page TCN-specific via tenantSlug check à un niveau supérieur).
 */
const AdminV2Pilotage = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const liveFeature = useFeature('pages.manager.live');
  const [params, setParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);

  const period = PERIODS.includes(params.get('period')) ? params.get('period') : 'month';
  const setPeriod = (p) => {
    const next = new URLSearchParams(params);
    next.set('period', p);
    setParams(next, { replace: true });
  };

  const { hero, indicators, tableData, loading, error, reload } = useAdminPilotageData({ period });

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Top bar */}
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                {t('adminV2.pilotage.title', 'Pilotage')}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {tenantName} · {t(`adminV2.periods.${period}`, period)}
              </p>
            </div>
            <PeriodSelector value={period} onChange={setPeriod} />
          </div>
          {liveFeature && <LiveStripStub />}
        </header>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-5 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{t('adminV2.pilotage.errorLoading', 'Failed to load data.')} {String(error.message || error)}</span>
              <button
                type="button"
                onClick={reload}
                className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium"
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          <HeroIntegrated period={period} {...hero} loading={loading} />

          {liveFeature && <LiveNowCardsStub />}

          <IndicatorsGrid {...indicators} totalCreators={hero.totalCreators} loading={loading} />

          <PilotageCreatorsTable data={tableData} loading={loading} period={period} />
        </div>
      </main>
    </div>
  );
};

export default AdminV2Pilotage;
